import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { FilterCombobox } from './FilterCombobox';
import { FeedbackFiltersState } from './FeedbackList';

interface FeedbackFiltersProps {
  filters: FeedbackFiltersState;
  onFilterChange: (filters: Partial<FeedbackFiltersState>) => void;
  onClearFilters: () => void;
  featureOptions: string[];
  customerOptions: string[];
  userOptions: string[];
}

export function FeedbackFilters({
  filters,
  onFilterChange,
  onClearFilters,
  featureOptions,
  customerOptions,
  userOptions,
}: FeedbackFiltersProps) {
  const handleInputChange = (
    field: keyof FeedbackFiltersState,
    value: string
  ) => {
    onFilterChange({ [field]: value });
  };

  const hasActiveFilters =
    filters.feature !== '' ||
    filters.vote !== 'all' ||
    filters.rating !== 'all' ||
    filters.user !== '' ||
    filters.customer !== '';

  return (
    <Card className="mb-4 border-transparent bg-transparent shadow-none">
      <CardContent className="px-0 pt-0">
        {hasActiveFilters && (
          <div className="mb-3 flex justify-end">
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              Clear Filters
            </Button>
          </div>
        )}
        {/* All five filters on one line, each box the same width. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-1.5">
            <Label className="text-xs">Feature</Label>
            <FilterCombobox
              ariaLabel="Filter by feature"
              value={filters.feature}
              onChange={(v) => handleInputChange('feature', v)}
              options={featureOptions}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Customer</Label>
            <FilterCombobox
              ariaLabel="Filter by customer"
              value={filters.customer}
              onChange={(v) => handleInputChange('customer', v)}
              options={customerOptions}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">User ID</Label>
            <FilterCombobox
              ariaLabel="Filter by user"
              value={filters.user}
              onChange={(v) => handleInputChange('user', v)}
              options={userOptions}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Sentiment</Label>
            <FilterCombobox
              ariaLabel="Filter by sentiment"
              value={filters.vote}
              onChange={(v) => handleInputChange('vote', v)}
              emptyValue="all"
              placeholder="All"
              options={[
                { value: 'up', label: 'Positive' },
                { value: 'down', label: 'Negative' },
              ]}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Rating</Label>
            <FilterCombobox
              ariaLabel="Filter by rating"
              value={filters.rating}
              onChange={(v) => handleInputChange('rating', v)}
              emptyValue="all"
              placeholder="All"
              options={[
                { value: '1', label: '1 Star' },
                { value: '2', label: '2 Stars' },
                { value: '3', label: '3 Stars' },
                { value: '4', label: '4 Stars' },
                { value: '5', label: '5 Stars' },
              ]}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
